import { ArrowLeft, GripVertical, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { TaskPriority, TaskTemplate, TaskType } from '../types/task';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface ChecklistItemForm {
  label: string;
  description: string;
  item_order: number;
  is_required: boolean;
  links_to_order_items: boolean;
}

const CreateEditTaskTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    task_type: TaskType.DELIVERY,
    description: '',
    default_instructions: '',
    default_priority: TaskPriority.NORMAL,
    estimated_duration_hours: '',
    requires_photo: false,
    requires_checklist_completion: true,
  });

  const [checklistItems, setChecklistItems] = useState<ChecklistItemForm[]>([]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);

      const isSupervisorOrAdmin = parsedUser.roles?.some((role: any) =>
        ['admin', 'supervisor'].includes(role.key)
      );

      if (!isSupervisorOrAdmin) {
        navigate('/');
        return;
      }
    }

    if (isEditMode) {
      loadTemplate();
    }
  }, [id, isEditMode, navigate]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        const template: TaskTemplate = data.data;
        setFormData({
          name: template.name,
          task_type: template.task_type,
          description: template.description || '',
          default_instructions: template.default_instructions || '',
          default_priority: template.default_priority,
          estimated_duration_hours: template.estimated_duration_hours?.toString() || '',
          requires_photo: template.requires_photo,
          requires_checklist_completion: template.requires_checklist_completion,
        });

        if (template.template_items) {
          setChecklistItems(
            template.template_items.map((item) => ({
              label: item.label,
              description: item.description || '',
              item_order: item.item_order,
              is_required: item.is_required,
              links_to_order_items: item.links_to_order_items,
            }))
          );
        }
      }
    } catch (error) {
      console.error('Error loading template:', error);
      alert('Failed to load template');
      navigate('/task-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChecklistItem = () => {
    const newOrder = checklistItems.length > 0 ? Math.max(...checklistItems.map((i) => i.item_order)) + 1 : 1;
    setChecklistItems([
      ...checklistItems,
      {
        label: '',
        description: '',
        item_order: newOrder,
        is_required: true,
        links_to_order_items: false,
      },
    ]);
  };

  const handleRemoveChecklistItem = (index: number) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleChecklistItemChange = (index: number, field: keyof ChecklistItemForm, value: any) => {
    const updated = [...checklistItems];
    updated[index] = { ...updated[index], [field]: value };
    setChecklistItems(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter a template name');
      return;
    }

    const itemsWithLabels = checklistItems.filter((item) => item.label.trim());
    if (itemsWithLabels.length !== checklistItems.length) {
      alert('Please fill in all checklist item labels or remove empty items');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      const payload = {
        name: formData.name.trim(),
        task_type: formData.task_type,
        description: formData.description.trim() || null,
        default_instructions: formData.default_instructions.trim() || null,
        default_priority: formData.default_priority,
        estimated_duration_hours: formData.estimated_duration_hours ? parseFloat(formData.estimated_duration_hours) : null,
        requires_photo: formData.requires_photo,
        requires_checklist_completion: formData.requires_checklist_completion,
        template_items: itemsWithLabels.map((item, idx) => ({
          label: item.label.trim(),
          description: item.description.trim() || null,
          item_order: idx + 1,
          is_required: item.is_required,
          links_to_order_items: item.links_to_order_items,
        })),
      };

      const url = isEditMode ? `/api/tasks/templates/${id}` : '/api/tasks/templates';
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        navigate('/task-templates');
      } else {
        alert(data.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('An error occurred while saving the template');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTaskType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="task-templates" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading template...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="task-templates" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/task-templates')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              {isEditMode ? 'Edit Template' : 'Create New Template'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>

                <div>
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Standard Procurement"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="task_type">Task Type *</Label>
                    <Select
                      value={formData.task_type}
                      onValueChange={(value) => setFormData({ ...formData, task_type: value as TaskType })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(TaskType).map((type) => (
                          <SelectItem key={type} value={type}>
                            {formatTaskType(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="default_priority">Default Priority *</Label>
                    <Select
                      value={formData.default_priority}
                      onValueChange={(value) =>
                        setFormData({ ...formData, default_priority: value as TaskPriority })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={TaskPriority.LOW}>Low</SelectItem>
                        <SelectItem value={TaskPriority.NORMAL}>Normal</SelectItem>
                        <SelectItem value={TaskPriority.HIGH}>High</SelectItem>
                        <SelectItem value={TaskPriority.URGENT}>Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this template..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="default_instructions">Default Instructions</Label>
                  <Textarea
                    id="default_instructions"
                    value={formData.default_instructions}
                    onChange={(e) => setFormData({ ...formData, default_instructions: e.target.value })}
                    placeholder="Default instructions for tasks created from this template..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label htmlFor="estimated_duration_hours">Estimated Duration (hours)</Label>
                  <Input
                    id="estimated_duration_hours"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimated_duration_hours}
                    onChange={(e) =>
                      setFormData({ ...formData, estimated_duration_hours: e.target.value })
                    }
                    placeholder="e.g., 4"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="requires_photo"
                    checked={formData.requires_photo}
                    onChange={(e) => setFormData({ ...formData, requires_photo: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="requires_photo" className="cursor-pointer">
                    Requires photo evidence
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="requires_checklist_completion"
                    checked={formData.requires_checklist_completion}
                    onChange={(e) =>
                      setFormData({ ...formData, requires_checklist_completion: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="requires_checklist_completion" className="cursor-pointer">
                    Require all checklist items to be completed
                  </Label>
                </div>
              </div>

              {/* Checklist Items */}
              <div className="space-y-4 pt-6 border-t">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Checklist Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddChecklistItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                {checklistItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground bg-muted/50 rounded-lg">
                    <p>No checklist items yet</p>
                    <p className="text-sm mt-1">Add items to create a structured checklist for this template</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {checklistItems.map((item, index) => (
                      <Card key={index} className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex gap-3">
                            <div className="flex items-start pt-2">
                              <GripVertical className="h-5 w-5 text-muted-foreground" />
                              <span className="text-sm font-medium text-muted-foreground ml-1">
                                {index + 1}
                              </span>
                            </div>

                            <div className="flex-1 space-y-3">
                              <Input
                                placeholder="Checklist item label *"
                                value={item.label}
                                onChange={(e) =>
                                  handleChecklistItemChange(index, 'label', e.target.value)
                                }
                                required
                              />

                              <Input
                                placeholder="Description (optional)"
                                value={item.description}
                                onChange={(e) =>
                                  handleChecklistItemChange(index, 'description', e.target.value)
                                }
                              />

                              <div className="flex gap-4">
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={item.is_required}
                                    onChange={(e) =>
                                      handleChecklistItemChange(index, 'is_required', e.target.checked)
                                    }
                                    className="h-4 w-4 rounded border-gray-300"
                                  />
                                  Required
                                </label>

                                {formData.task_type === TaskType.PROCUREMENT && (
                                  <label className="flex items-center gap-2 text-sm">
                                    <input
                                      type="checkbox"
                                      checked={item.links_to_order_items}
                                      onChange={(e) =>
                                        handleChecklistItemChange(
                                          index,
                                          'links_to_order_items',
                                          e.target.checked
                                        )
                                      }
                                      className="h-4 w-4 rounded border-gray-300"
                                    />
                                    Links to order items
                                  </label>
                                )}
                              </div>
                            </div>

                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveChecklistItem(index)}
                              className="flex-shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/task-templates')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : isEditMode ? 'Update Template' : 'Create Template'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CreateEditTaskTemplate;

